import React, { useState } from "react";
import { Menu, X, Download, Landmark, Briefcase, Globe } from "lucide-react";
import Modal from "./Modal";
import { useTranslation } from 'react-i18next';

import { ViewMode } from '../App';

interface HeaderProps {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  lastHomeMode?: 'pessoal' | 'empresa';
}

const Header: React.FC<HeaderProps> = ({ viewMode, setViewMode, lastHomeMode }) => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Current short language string, safely handling language variants like pt-BR
  const currentLang = (i18n.language || 'pt').substring(0, 2); 

  const toggleLanguage = () => {
    const newLang = currentLang === 'pt' ? 'en' : 'pt';
    i18n.changeLanguage(newLang);
  };

  const scrollToSection = (sectionId: string, forceMode: 'pessoal' | 'empresa' = 'pessoal') => {
    if (!['pessoal', 'empresa'].includes(viewMode)) {
      setViewMode(forceMode);
      // Aguardar a renderização da página principal antes de fazer scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-orange-100 shadow-sm">
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentLang === 'pt' ? "Em Breve" : "Coming Soon"}
      >
        <div className="space-y-4">
          <p>
            {currentLang === 'pt' 
              ? "O aplicação MotoRush estará disponível em breve para iOS e Android. Fique atento às novidades!"
              : "The MotoRush app will be available soon for iOS and Android. Stay tuned!"}
          </p>
          <div className="pt-4 border-t border-gray-100">
            <a 
              href="https://wa.me/244954009112"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all duration-300 font-bold shadow-md hover:shadow-green-200"
            >
              <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.966C16.59 1.988 14.113.96 11.49.959c-5.442 0-9.866 4.372-9.87 9.802 0 1.63.45 3.224 1.302 4.633L1.87 20.887l5.51-1.442.267.159zm11.23-6.805c-.328-.164-1.94-.959-2.24-1.07-.3-.109-.52-.164-.74.164-.22.329-.85 1.07-1.04 1.29-.19.22-.38.24-.7.08-.328-.164-1.386-.51-2.64-1.627-.975-.87-1.633-1.946-1.824-2.274-.19-.329-.02-.507.143-.67.147-.146.328-.382.49-.574.164-.19.22-.328.328-.548.11-.22.055-.41-.03-.574-.08-.164-.74-1.78-.99-2.41-.247-.592-.5-.509-.685-.519-.178-.009-.383-.01-.588-.01-.205 0-.537.077-.82.383-.283.307-1.082 1.057-1.082 2.578 0 1.52 1.107 2.99 1.26 3.2 1.764 2.455 3.415 3.659 5.86 4.605 2.445.945 2.445.63 2.885.589.44-.041 1.94-.794 2.213-1.52.274-.727.274-1.348.192-1.472-.083-.124-.302-.197-.63-.362z"/>
              </svg>
              <span>{currentLang === 'pt' ? 'Falar pelo WhatsApp (954009112)' : 'Chat on WhatsApp (954009112)'}</span>
            </a>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        {/* Logo and Mode Selector */}
        <div className="flex items-center gap-3 sm:gap-6">
          <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#FF6B35] to-[#4A90E2] bg-clip-text text-transparent cursor-pointer shrink-0" onClick={() => scrollToSection("root", lastHomeMode || 'empresa')}>
            MotoRush
          </span>

          {/* Toggle Switch Capsule or Back Button */}
          {['pessoal', 'empresa'].includes(viewMode) ? (
            <div className="flex bg-gray-100 rounded-full p-0.5 border border-gray-200 shadow-inner scale-90 sm:scale-100 origin-left">
              <button
                onClick={() => setViewMode('pessoal')}
                className={`px-2.5 py-1 sm:px-3.5 rounded-full text-[10px] sm:text-xs font-bold transition-all flex items-center gap-1 ${
                  viewMode === 'pessoal'
                    ? 'bg-[#FF6B35] text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Briefcase size={10} className="sm:w-3 sm:h-3" />
                <span className="hidden sm:inline">{t('header.modePersonnel')}</span>
                <span className="sm:hidden">{viewMode === 'pessoal' ? t('header.modePersonnel').substring(0,4) : 'Pess'}</span>
              </button>
              <button
                onClick={() => setViewMode('empresa')}
                className={`px-2.5 py-1 sm:px-3.5 rounded-full text-[10px] sm:text-xs font-bold transition-all flex items-center gap-1 ${
                  viewMode === 'empresa'
                    ? 'bg-[#FF6B35] text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Landmark size={10} className="sm:w-3 sm:h-3" />
                <span className="hidden sm:inline">{t('header.modeCorporate')}</span>
                <span className="sm:hidden">{viewMode === 'empresa' ? t('header.modeCorporate').substring(0,3) : 'Emp'}</span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setViewMode(lastHomeMode || 'empresa')}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              {currentLang === 'pt' ? 'Voltar ao Início' : 'Back to Home'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
            {/* Navigation Desktop */}
            <nav className="hidden md:flex items-center gap-8 mr-2">
            {viewMode === 'empresa' ? (
                <>
                <button
                    onClick={() => scrollToSection("corporate-sla", "empresa")}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {t('header.benefits')} SLA
                </button>
                <button
                    onClick={() => {
                        setViewMode('about');
                        setIsMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {currentLang === 'pt' ? 'Quem Somos' : 'About Us'}
                </button>
                <button
                    onClick={() => scrollToSection("b2b-lead-form", "empresa")}
                    className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white px-5 py-2 rounded-full font-bold transition-all shadow-md hover:shadow-orange-200 flex items-center gap-2 transform hover:-translate-y-0.5 text-sm"
                >
                    {t('header.scheduleDemo')} B2B
                </button>
                </>
            ) : (
                <>
                <button
                    onClick={() => scrollToSection("download", "pessoal")}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {t('header.services')}
                </button>
                <button
                    onClick={() => {
                        setViewMode('about');
                        setIsMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {currentLang === 'pt' ? 'Quem Somos' : 'About Us'}
                </button>
                <button
                    onClick={() => scrollToSection("how-it-works", "pessoal")}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {t('header.howItWorks')}
                </button>
                <button
                    onClick={() => scrollToSection("register", "pessoal")}
                    className="text-gray-700 hover:text-[#FF6B35] font-semibold transition-colors text-sm"
                >
                    {t('header.contact')}
                </button>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white px-5 py-2 rounded-full font-bold transition-all shadow-md hover:shadow-orange-200 flex items-center gap-2 transform hover:-translate-y-0.5 text-sm"
                >
                    <Download size={16} />
                    {t('header.downloadApp')}
                </button>
                </>
            )}
            </nav>

            {/* Global Language Selector Button - Made ultra compact on mobile */}
            <button 
                onClick={toggleLanguage}
                className="flex items-center justify-center gap-1 w-8 h-8 sm:w-auto sm:px-3 sm:py-1.5 border border-gray-200 hover:border-orange-200 bg-gray-50 hover:bg-orange-50 text-gray-600 hover:text-[#FF6B35] rounded-full transition-all duration-300 text-xs font-bold shadow-sm group shrink-0"
                title={currentLang === 'pt' ? "Switch to English" : "Mudar para Português"}
            >
                <Globe size={14} className="group-hover:rotate-12 transition-transform shrink-0" />
                <span className="hidden sm:inline uppercase tracking-wider">{currentLang}</span>
            </button>

            {/* Mobile Menu Button */}
            {['pessoal', 'empresa'].includes(viewMode) && (
              <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden text-[#FF6B35]"
              >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-orange-100 p-4 shadow-lg">
          <nav className="flex flex-col gap-4">
            {viewMode === 'empresa' ? (
              <>
                <button
                  onClick={() => scrollToSection("corporate-sla", "empresa")}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {t('header.benefits')} SLA
                </button>
                <button
                  onClick={() => {
                      setViewMode('about');
                      setIsMenuOpen(false);
                  }}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {currentLang === 'pt' ? 'Quem Somos' : 'About Us'}
                </button>
                <button
                  onClick={() => scrollToSection("b2b-lead-form", "empresa")}
                  className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 text-sm"
                >
                  {t('header.scheduleDemo')} B2B
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => scrollToSection("download", "pessoal")}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {t('header.services')}
                </button>
                <button
                  onClick={() => {
                      setViewMode('about');
                      setIsMenuOpen(false);
                  }}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {currentLang === 'pt' ? 'Quem Somos' : 'About Us'}
                </button>
                <button
                  onClick={() => scrollToSection("how-it-works", "pessoal")}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {t('header.howItWorks')}
                </button>
                <button
                  onClick={() => scrollToSection("register", "pessoal")}
                  className="text-left text-gray-700 font-semibold py-2 text-sm"
                >
                  {t('header.contact')}
                </button>
                <button
                  onClick={() => {
                    setIsModalOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 text-sm"
                >
                  <Download size={16} />
                  {t('header.downloadApp')}
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
